import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Form, Formik } from "formik";
import { constants } from "@qgiv/core-js";
import { SEO, UpdateFieldErrorFocus } from "@qgiv/core-donor";
import {
    getShouldSetRemainingFormToShow,
    prepareRegistrationValuesToSave,
    submitParticipantAction,
} from "@qgiv/p2p-registration";
import { useDispatch } from "react-redux";
import RegistrationBase from "../templates/RegistrationBase";
import ConnectedRegistrationPageContent from "./ConnectedRegistrationPageContent";
import ConnectedTeamModal from "./ConnectedTeamModal";
import ConnectedUpdateIsNfp from "./ConnectedUpdateIsNfp";
import ConnectedUpdateFields from "./ConnectedUpdateFields";
import ConnectedUpdateFCRegistration from "./ConnectedUpdateFCRegistration";
import {
    getInitialTouched,
    getInitialValues,
    getValidationSchema,
} from "./formik";
import ConnectedEarlyRegistrationSubmit from "../../common/EarlyRegistrationSubmit/ConnectedEarlyRegistrationSubmit";
import ErrorDisplay from "../../common/ErrorDisplay";
import {
    getIfEmailExistsInCurrentRegistration,
    handleVerificationError,
    handleVerificationSuccess,
} from "../../../redux/utilities/emailVerificationHelpers";

const RegistrationPage = ({
    // Props from store
    allowSubRegistrantToChangeRegRole,
    alias,
    baseUrl,
    commitmentDetails,
    currentRegistration,
    currentPage,
    emailVerification,
    primaryRegistrationValues = {},
    savedRegistrationValues = {},
    remainingUnconfirmedRegistrations = [],
    settings,
    redirect = "",
    validationSettings,
    allowEarlyFormSubmissionRegistrationPage,
    hasParentRegistration,
    displayStore,
    erroredEntities = [],
    getShouldUpdateAdditionalRegToMatchNewPrimaryTeam,
    registrations = [],
    // Actions
    addRegistration,
    updateCurrentPage,
    updateCurrentRegistration,
    updateRegistration,
    setCurrentRegistrationCategory,
    setAppShowRemainingForm,
    updateLastVerifiedEmail,
    updateEmailVerificationInFlight,
    updateEmailHasBeenVerified,
    updateEmailExistsInCurrentRegistration,
    updateEmailExistsInPreviousRegistration,
    updateScrollToEmailField,
    updateYourFundraisingDashboard,
    // React router props
    // location,
    documentTitle,
    // items needed to re-run category filter
    Classification = [],
    staticValuesForCategoryFilter,
    staticValuesForCategoryFlags,
    getCategoryFilteringFlags,
    filterCategoriesBasedOnPresetsOrUserSelections,
}) => {
    const {
        ENUMS: {
            DefaultView: { REGISTRATION_DETAILS_STEP },
            GlobalAccountAction,
        },
    } = constants;

    const dispatch = useDispatch();

    const { fields } = settings;
    const [isVisible, setIsVisible] = useState(false);
    const [submitFullForm, setSubmitFullForm] = useState(false);
    const [errors, setErrors] = useState([]);

    const navigate = useNavigate();

    // Need to wrap redux actions in dispatch before they are passed to
    // common helper functions
    const dispatchUpdateEmailHasBeenVerified = (_emailHasBeenVerified) => {
        dispatch(updateEmailHasBeenVerified(_emailHasBeenVerified));
    };
    const dispatchUpdateEmailVerificationInFlight = (
        _verificationIsInFlight,
    ) => {
        dispatch(updateEmailVerificationInFlight(_verificationIsInFlight));
    };
    const dispatchUpdateLastVerifiedEmail = (_lastVerifiedEmail) => {
        dispatch(updateLastVerifiedEmail(_lastVerifiedEmail));
    };
    const dispatchUpdateEmailExistsInPreviousRegistration = (
        _emailExistsInPreviousRegistration,
    ) =>
        dispatch(
            updateEmailExistsInPreviousRegistration(
                _emailExistsInPreviousRegistration,
            ),
        );

    const dispatchUpdateYourFundraisingDashboard = (
        _yourFundraisingDashboard,
    ) => {
        dispatch(updateYourFundraisingDashboard(_yourFundraisingDashboard));
    };

    const {
        lastVerifiedEmail,
        emailExistsInCurrentRegistration,
        emailExistsInPreviousRegistration,
    } = emailVerification;

    useEffect(() => {
        if (currentPage !== REGISTRATION_DETAILS_STEP) {
            dispatch(updateCurrentPage(REGISTRATION_DETAILS_STEP));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    // we need to break this up into smaller pieces
    const handleRegistrationPassedEmailVerification = ({
        values,
        resetForm,
        setSubmitting,
    }) => {
        const { Add_New_Registration = false } = values;
        const valuesToSaveForThisCurrentRegistrant =
            prepareRegistrationValuesToSave({
                _fields: fields,
                incomingValues: values,
                commitmentDetails,
            });
        setSubmitting(true);

        setTimeout(() => {
            const nextRegistrationIndex = currentRegistration + 1;
            // 1. Store values in redux store via dispatching an action
            const currentRegistrationValues = {
                index: currentRegistration,
                registration: valuesToSaveForThisCurrentRegistrant,
            };
            dispatch(updateRegistration(currentRegistrationValues));

            // If there are multiple registrations and teams are limited to matching the Primary
            // and Primary has changed their team, then we should update the other registrations
            // with the new Primary Team
            const shouldUpdateAdditionalRegToMatchNewPrimaryTeam =
                getShouldUpdateAdditionalRegToMatchNewPrimaryTeam(values);

            if (shouldUpdateAdditionalRegToMatchNewPrimaryTeam) {
                // Update other registrations Teams
                registrations.forEach((reg, i) => {
                    if (i > 0 && Object.keys(reg).length > 0) {
                        dispatch(
                            updateRegistration({
                                index: i,
                                registration: {
                                    Team: values.Team,
                                    Registration_Role: values.Registration_Role,
                                    Needs_To_Be_Reviewed: true,
                                },
                            }),
                        );
                    }
                });
            }
            // 3. Determine where to navigate user
            if (Add_New_Registration) {
                // Add a new registration to store
                // Set the new registration index
                const isPrimaryThisSubmission = currentRegistration === 0;
                const valuesToUseAsPrimaryRegistration = isPrimaryThisSubmission
                    ? valuesToSaveForThisCurrentRegistrant
                    : primaryRegistrationValues;
                const newRegistration = !allowSubRegistrantToChangeRegRole
                    ? {
                          Team: valuesToUseAsPrimaryRegistration.Team,
                          Registration_Role:
                              valuesToUseAsPrimaryRegistration.Registration_Role,
                          Needs_To_Be_Reviewed: false,
                      }
                    : {};

                dispatch(addRegistration(newRegistration));
                // Reload form with new state
                window.scrollTo(0, 0);

                // settings has a category filter which is outdated
                // need to re-run that filter. Buckle-up.

                // add the next registrations preset values to the registrations array
                const newRegistrations = [...registrations, newRegistration];
                // current registrations array does not contain THIS registration
                // so we need to set it in the newRegistrations by index
                // for the category filter
                newRegistrations[currentRegistration] =
                    valuesToSaveForThisCurrentRegistrant;
                const {
                    hasClassifications,
                    hasPreSelectedTeamCaptain,
                    hasPreSelectedTeamMember,
                } = getCategoryFilteringFlags({
                    ...staticValuesForCategoryFlags,
                    Classification,
                    currentRegistration: nextRegistrationIndex,
                    values: valuesToUseAsPrimaryRegistration,
                });

                const categoriesFilteredBySettingsAndUserSelections =
                    filterCategoriesBasedOnPresetsOrUserSelections({
                        ...staticValuesForCategoryFilter,
                        Classification,
                        hasClassifications,
                        hasPreSelectedTeamCaptain,
                        hasPreSelectedTeamMember,
                        isChildParticipant: true,
                        registrations: newRegistrations,
                    });

                const newSettings = {
                    ...settings,
                    categoriesFilteredBySettingsAndUserSelections,
                };

                // might need to re-eval savedRegistrationValues
                // with the one we just added
                const newInitialValues = getInitialValues(
                    newSettings,
                    newRegistration,
                    isPrimaryThisSubmission
                        ? valuesToSaveForThisCurrentRegistrant
                        : primaryRegistrationValues,
                    allowSubRegistrantToChangeRegRole,
                );
                const newInitialTouched = getInitialTouched(
                    settings,
                    newRegistration,
                );

                dispatch(setAppShowRemainingForm(false));

                resetForm({
                    values: newInitialValues,
                    // Email touched is being overwritten by EmailRegistrationField useEffect
                    // calling handleEmailBlur
                    touched: { ...newInitialTouched, Email: false },
                    errors: {},
                });
                // If Category and Full Registration Roles are present in new initial value then
                // we should show the remaining form, otherwise hide it to await selections.
                const shouldSetRemainingFormToShow =
                    getShouldSetRemainingFormToShow(newInitialValues);

                dispatch(setAppShowRemainingForm(shouldSetRemainingFormToShow));
                setSubmitting(false);
            } else {
                // There are registrations outside of the current registration that need to be reviewed
                if (remainingUnconfirmedRegistrations.length > 0) {
                    // need to find "next" unconfirmed registration from registrations
                    const nextUnconfirmedRegistration =
                        remainingUnconfirmedRegistrations[0];
                    const { registrationIndex } = nextUnconfirmedRegistration;
                    const registrationToDisplay =
                        registrations[registrationIndex];
                    // toggle the remaining form to show so currency inputs are reset
                    dispatch(setAppShowRemainingForm(false));
                    // set the form values with those values
                    resetForm({
                        values: registrationToDisplay,
                    });

                    // update current registration
                    dispatch(updateCurrentRegistration(registrationIndex));

                    // set the remaining form to show if criteria is met
                    setTimeout(() => {
                        const shouldSetRemainingFormToShow =
                            getShouldSetRemainingFormToShow(
                                registrationToDisplay,
                            );

                        dispatch(
                            setAppShowRemainingForm(
                                shouldSetRemainingFormToShow,
                            ),
                        );
                    }, 100);

                    // setSubmitting false
                    setSubmitting(false);
                    return;
                }

                // Products are arranged in Categories and
                // there will always be one category even if there are no products
                if (displayStore) {
                    // Navigate to store page only if products are available on Registration
                    window.scrollTo(0, 0);
                    navigate(`${baseUrl}/form/store`);
                } else {
                    // Navigate to payment page
                    if (!allowEarlyFormSubmissionRegistrationPage) {
                        window.scrollTo(0, 0);
                        navigate(`${baseUrl}/form/payment`);
                        return;
                    }
                    setSubmitFullForm(true);
                }
            }
        }, 500);
    };

    const onSubmit = (values, { resetForm, setFieldError, setSubmitting }) => {
        const { Email, Managed_By_Primary, Is_Nfp } = values;
        setSubmitting(true);
        if (!Managed_By_Primary && !Is_Nfp) {
            // check Email value against current registrations
            // check Email value against lastVerifiedEmail
            const currentEmailExistsInCurrentRegistration =
                getIfEmailExistsInCurrentRegistration({
                    currentRegistration,
                    registrations,
                    Email,
                });

            if (
                currentEmailExistsInCurrentRegistration ||
                lastVerifiedEmail !== Email
            ) {
                // dispatch current registration values
                dispatch(
                    updateEmailExistsInCurrentRegistration(
                        currentEmailExistsInCurrentRegistration,
                    ),
                );
                // make the api call
                const data = {
                    Email,
                    Global_Account_Action:
                        GlobalAccountAction.SEARCH_FOR_EXISTENCE,
                };
                const participantAction = `/event/${alias}/register/account/participantaction/`;

                // this value disables the next buttons actions
                dispatch(updateEmailVerificationInFlight(true));

                submitParticipantAction(participantAction, data)
                    .then((response) => {
                        // set verification data in redux
                        handleVerificationSuccess({
                            dispatchUpdateLastVerifiedEmail,
                            dispatchUpdateEmailVerificationInFlight,
                            dispatchUpdateEmailHasBeenVerified,
                            dispatchUpdateEmailExistsInPreviousRegistration,
                            dispatchUpdateYourFundraisingDashboard,
                            Email,
                            response,
                            setFieldError,
                        });
                        // check if email verification allows page submission
                        const { success } = response.data;
                        if (success) {
                            const { validation } = response.data;
                            const { previousRegistrationHasValidationError } =
                                validation;
                            if (
                                !previousRegistrationHasValidationError &&
                                !currentEmailExistsInCurrentRegistration
                            ) {
                                // we did it, we passed email verification
                                handleRegistrationPassedEmailVerification({
                                    values,
                                    resetForm,
                                    setSubmitting,
                                });
                            } else {
                                setSubmitting(false);
                                dispatch(updateScrollToEmailField(true));
                            }
                        }
                    })
                    .catch((error) => {
                        handleVerificationError({
                            dispatchUpdateLastVerifiedEmail,
                            dispatchUpdateEmailVerificationInFlight,
                            dispatchUpdateEmailHasBeenVerified,
                            dispatchUpdateYourFundraisingDashboard,
                            Email,
                            error,
                            setSubmitting,
                        });
                        // I think this is the one that is not running correctly on mobile
                        dispatch(updateScrollToEmailField(true));
                    });
                return;
            }

            // check if we have known email problems
            if (
                emailExistsInCurrentRegistration ||
                emailExistsInPreviousRegistration
            ) {
                // this one definitely works correctly on mobile
                dispatch(updateScrollToEmailField(true));
                setSubmitting(false);
                return;
            }
        }
        // we did it, we passed email verification
        handleRegistrationPassedEmailVerification({
            values,
            resetForm,
            setSubmitting,
        });
    };

    const formikProps = {
        initialTouched: getInitialTouched(settings, savedRegistrationValues),
        initialValues: getInitialValues(
            settings,
            savedRegistrationValues,
            primaryRegistrationValues,
            allowSubRegistrantToChangeRegRole,
        ),
        validationSchema: getValidationSchema(validationSettings),
        onSubmit,
    };

    // -------------------------------------------------------------------------
    // MARK: Navigation useEffects
    // -------------------------------------------------------------------------
    // only used to navigate to classification page
    useEffect(() => {
        if (redirect) {
            navigate(redirect);
        }
    }, [redirect, navigate]);

    useEffect(() => {
        // This prevents flash of outdated content from displaying
        setIsVisible(true);
        // These flags need to be reset on page load
        // since they can be active from a previous registration
        dispatch(setCurrentRegistrationCategory([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return isVisible ? (
        <Formik {...formikProps}>
            <RegistrationBase>
                <SEO title={documentTitle} description="" />
                <ErrorDisplay
                    errors={errors}
                    setErrors={setErrors}
                    erroredEntities={erroredEntities}
                />
                <Form>
                    <ConnectedUpdateIsNfp />
                    <ConnectedUpdateFields />
                    {hasParentRegistration && <ConnectedUpdateFCRegistration />}
                    <ConnectedRegistrationPageContent />
                    <ConnectedTeamModal />
                    {submitFullForm && (
                        <ConnectedEarlyRegistrationSubmit
                            setErrors={setErrors}
                        />
                    )}
                    <UpdateFieldErrorFocus />
                </Form>
            </RegistrationBase>
        </Formik>
    ) : null;
};

RegistrationPage.propTypes = {
    allowSubRegistrantToChangeRegRole: PropTypes.bool.isRequired,
    alias: PropTypes.string.isRequired,
    // Add registration redux action
    addRegistration: PropTypes.func.isRequired,
    // Flag when total of registration is $0 and can early submit form
    allowEarlyFormSubmissionRegistrationPage: PropTypes.bool.isRequired,
    // Current registration index
    currentRegistration: PropTypes.number.isRequired,
    // Current page
    currentPage: PropTypes.number.isRequired,
    // Flag to only display store nav link if active reg products
    displayStore: PropTypes.bool.isRequired,
    // email
    emailVerification: PropTypes.shape({
        lastVerifiedEmail: PropTypes.string,
        emailExistsInCurrentRegistration: PropTypes.bool,
        emailExistsInPreviousRegistration: PropTypes.bool,
    }).isRequired,
    // Array of outdated entities used for Error Redirect
    erroredEntities: PropTypes.arrayOf(PropTypes.shape({})),
    // Saved values from primary registration
    primaryRegistrationValues: PropTypes.shape({}),
    // Optional redirect object
    redirect: PropTypes.string,
    // Array of registrations that need to confirm details (invited, pending, race conditions)
    remainingUnconfirmedRegistrations: PropTypes.arrayOf(
        PropTypes.shape({ registrationIndex: PropTypes.number }),
    ),
    // Object of saved registration values for pre-filling
    savedRegistrationValues: PropTypes.shape({}),
    // Form and event settings object
    settings: PropTypes.shape({
        categories: PropTypes.arrayOf(PropTypes.shape({})),
        classifications: PropTypes.arrayOf(PropTypes.shape({})),
        fields: PropTypes.arrayOf(PropTypes.shape({})),
        formSettings: PropTypes.shape({}),
        p2pSettings: PropTypes.shape({}),
    }).isRequired,
    // Object to be passed to validation logic
    validationSettings: PropTypes.shape({
        phoneFieldsType: PropTypes.number.isRequired,
        isChildParticipant: PropTypes.bool.isRequired,
        minParticipantGoal: PropTypes.number.isRequired,
        minTeamGoal: PropTypes.number.isRequired,
    }).isRequired,
    // Redux action to update current registration index
    updateCurrentRegistration: PropTypes.func.isRequired,
    // Redux action to update current page enum
    updateCurrentPage: PropTypes.func.isRequired,
    // Redux action to update an existing registration object
    updateRegistration: PropTypes.func.isRequired,
    // Redux action to update is we should display form sections after role selection
    setAppShowRemainingForm: PropTypes.func.isRequired,
    // Redux action to update selected category of registrations[currentRegistration]
    setCurrentRegistrationCategory: PropTypes.func.isRequired,
    // String that contains the document title that appears in the browser tab
    documentTitle: PropTypes.string.isRequired,
    // Flag for if registration comes from fundraising center registration
    hasParentRegistration: PropTypes.bool.isRequired,
    baseUrl: PropTypes.string.isRequired,
    commitmentDetails: PropTypes.shape({}).isRequired,
    getShouldUpdateAdditionalRegToMatchNewPrimaryTeam:
        PropTypes.func.isRequired,
    registrations: PropTypes.arrayOf(PropTypes.shape({})),
    updateLastVerifiedEmail: PropTypes.func.isRequired,
    updateEmailVerificationInFlight: PropTypes.func.isRequired,
    updateEmailHasBeenVerified: PropTypes.func.isRequired,
    updateEmailExistsInCurrentRegistration: PropTypes.func.isRequired,
    updateEmailExistsInPreviousRegistration: PropTypes.func.isRequired,
    updateScrollToEmailField: PropTypes.func.isRequired,
    updateYourFundraisingDashboard: PropTypes.func.isRequired,
    // values needed to re-run category filtering
    Classification: PropTypes.arrayOf(PropTypes.string),
    staticValuesForCategoryFilter: PropTypes.shape({}).isRequired,
    staticValuesForCategoryFlags: PropTypes.shape({}).isRequired,
    getCategoryFilteringFlags: PropTypes.func.isRequired,
    filterCategoriesBasedOnPresetsOrUserSelections: PropTypes.func.isRequired,
};

export default RegistrationPage;
